import { Buffer } from "buffer";
import { VersionedTransaction } from "@solana/web3.js";
import nacl from "tweetnacl";
import { LIGHTHOUSE_PROGRAM, validLighthouseAssertion } from "./lighthouse.js";
export { LIGHTHOUSE_PROGRAM, ASSERTION_PACKET_RESERVE, APPROVAL_COMPUTE_UNITS, validLighthouseAssertion } from "./lighthouse.js";
export const WALLET_POLICY_VERSION = "LIGHTHOUSE_V1";
export class WalletPolicyError extends Error {
    reason;
    addedProgramIds;
    code = "wallet_approval_incompatible";
    status = 409;
    retryable = false;
    constructor(reason, addedProgramIds = []) {
        super("Your wallet returned an approval we could not validate. Refresh to check its status before trying again.");
        this.reason = reason;
        this.addedProgramIds = addedProgramIds;
    }
}
export function assertWalletMessage(before, after, tables = []) {
    const programs = (message) => new Set(message.compiledInstructions.map(ix => message.staticAccountKeys[ix.programIdIndex]?.toBase58()).filter((id) => !!id));
    const originalPrograms = programs(before);
    let addedPrograms = [...programs(after)].filter(id => !originalPrograms.has(id));
    const reject = (reason) => { throw new WalletPolicyError(reason, addedPrograms); };
    if (before.version !== after.version || before.recentBlockhash !== after.recentBlockhash)
        reject("version_or_blockhash");
    const resolve = (message) => {
        try {
            const keys = message.getAccountKeys({ addressLookupTableAccounts: [...tables] });
            return Array.from({ length: keys.length }, (_, i) => keys.get(i));
        }
        catch {
            return reject("lookup_unavailable");
        }
    };
    const a = resolve(before), b = resolve(after);
    if (!a[0] || a[0].toBase58() !== b[0]?.toBase58())
        reject("payer");
    const indexes = (keys) => new Map(keys.map((key, i) => [key.toBase58(), i]));
    const ai = indexes(a), bi = indexes(b);
    if (ai.size !== a.length || bi.size !== b.length)
        reject("duplicate_accounts");
    for (const [key, i] of ai) {
        const j = bi.get(key);
        if (j === undefined || before.isAccountSigner(i) !== after.isAccountSigner(j) || before.isAccountWritable(i) !== after.isAccountWritable(j))
            reject("permissions");
    }
    const normalize = (message, keys) => message.compiledInstructions.map(ix => ({
        program: keys[ix.programIdIndex]?.toBase58(), accounts: ix.accountKeyIndexes.map(i => keys[i]?.toBase58()), data: Buffer.from(ix.data).toString("hex"),
    }));
    const original = normalize(before, a), returned = normalize(after, b);
    addedPrograms = [...new Set(returned.map(ix => ix.program).filter(program => !original.some(ix => ix.program === program)))];
    let cursor = 0, assertions = 0;
    const assertionKeys = new Set();
    // Instructions-sysvar consumers may bind absolute indexes; appended guards
    // cannot shift an existing proof or its consumer.
    const indexed = ai.has("Sysvar1nstructions1111111111111111111111111");
    for (let i = 0; i < returned.length; i++) {
        if (JSON.stringify(returned[i]) === JSON.stringify(original[cursor])) {
            cursor++;
            continue;
        }
        const ix = after.compiledInstructions[i];
        if (returned[i].program !== LIGHTHOUSE_PROGRAM || indexed && cursor < original.length
            || ++assertions > 16 || !validLighthouseAssertion(ix.data, ix.accountKeyIndexes.length))
            reject("unsupported_instruction");
        assertionKeys.add(LIGHTHOUSE_PROGRAM);
        for (const account of returned[i].accounts) {
            if (!account)
                reject("account_index");
            assertionKeys.add(account);
        }
    }
    if (cursor !== original.length)
        reject("business_instructions");
    for (const [key, i] of bi)
        if (!ai.has(key) && (!assertionKeys.has(key) || after.isAccountWritable(i) || after.isAccountSigner(i)))
            reject("added_permissions");
    if (before.header.numRequiredSignatures !== after.header.numRequiredSignatures)
        reject("signers");
}
export function verifyTransactionSignatures(tx, ownerOnly = false) {
    const count = ownerOnly ? 1 : tx.message.header.numRequiredSignatures;
    const bytes = tx.message.serialize();
    for (let i = 0; i < count; i++)
        if (!tx.signatures[i] || !nacl.sign.detached.verify(bytes, tx.signatures[i], tx.message.staticAccountKeys[i].toBytes()))
            throw new WalletPolicyError("signature");
}
export function assertTransactionPolicy(prepared, signed, ownerOnly = false, tables = []) {
    const before = VersionedTransaction.deserialize(Buffer.from(prepared, "base64"));
    const after = VersionedTransaction.deserialize(Buffer.from(signed, "base64"));
    assertWalletMessage(before.message, after.message, tables);
    verifyTransactionSignatures(after, ownerOnly);
    if (after.serialize().length > 1232)
        throw new WalletPolicyError("packet_limit");
    return after;
}
export function ownerApprovalBytes(prepared) {
    const tx = VersionedTransaction.deserialize(Buffer.from(prepared, "base64"));
    if (tx.signatures[0]?.some(b => b !== 0))
        throw new WalletPolicyError("already_signed");
    return Buffer.from(new VersionedTransaction(tx.message).serialize()).toString("base64");
}
